<template>
  <div v-if="userData">
    <div class="gl-forms__container">
      <div class="gl-forms__title">
        {{ $t('profile.info') }}
      </div>
      <div class="gl-grid-form">
        <div class="form__group col-2">
          <gl-input
            id="profile.name"
            v-model.trim="$v.form.name.$model"
            v-skeletor:onChange="setValue"
            :type="'text'"
            :label="$t('nouns.firstName')"
            :placeholder="$t('nouns.firstName')"
            :mandatory="true"
            :value="$v.form.name.$model"
            :errormessage="
              !$v.form.name.required && $v.form.name.$model !== null
                ? $t('account.errors.fieldRequired')
                : null
            "
          >
          </gl-input>
        </div>
        <div class="form__group col-2">
          <gl-input
            id="profile.surname"
            v-model.trim="$v.form.surname.$model"
            v-skeletor:onChange="setValue"
            :type="'text'"
            :label="$t('nouns.' + Object.keys(form)[1])"
            :placeholder="$t('nouns.' + Object.keys(form)[1])"
            :mandatory="true"
            :value="$v.form.surname.$model"
          >
          </gl-input>
        </div>
        <div class="form__group col-1">
          <gl-input
            id="profile.email"
            v-model.trim="$v.form.email.$model"
            v-skeletor:onChange="setValue"
            :type="'email'"
            :label="$t('nouns.' + Object.keys(form)[2])"
            :placeholder="$t('nouns.' + Object.keys(form)[2])"
            :value="$v.form.email.$model"
            :readonly="true"
            :errormessage="
              !$v.form.email.required && $v.form.email.$model !== null
                ? $t('account.errors.fieldRequired')
                : $v.form.email.$error || $v.form.email.$invalid
                ? errorMessages.email
                : null
            "
          >
          </gl-input>
        </div>
        <div class="form__group col-2-fullrow">
          <gl-select
            id="profile.lang"
            v-skeletor:onChange="setValue"
            :label="$t('account.forms.selectLang')"
            :placeholder="$t('account.forms.selectLang')"
            :options.prop="getLanguages"
            :mandatory="true"
            :value.prop="$v.form.lang.$model"
            :separator="true"
            :errormessage="$v.form.lang.$error ? $t('account.errors.fieldRequired') : null"
          >
          </gl-select>
        </div>

        <div class="form__group col-2-fullrow">
          <gl-input
            id="profile.surname"
            v-skeletor:onChange="setValue"
            :type="'text'"
            :label="$t('nouns.password')"
            :placeholder="$t('nouns.password')"
            :icon="'glicon-edit'"
            :editable="false"
            :readonly="true"
            :selector="true"
            :value="'************'"
            @onClick="editPassword('password')"
          >
          </gl-input>
        </div>
      </div>
    </div>

    <!-- Notifications configuration -->
    <template v-if="shouldShowNotificationsCfg">
      <div class="gl-forms__container">
        <div class="gl-forms__title">
          {{ $t('admins.notifications') }}
        </div>
        <!-- na2 -->
        <template v-if="shouldShowNa2Cfg">
          <div class="form__row">
            <div class="account__block--checkbox">
              <gl-checkbox
                id="profile.notifications.na2.enabled"
                v-skeletor:onChange="setNotifications"
                :label="$t('admins.mailResumeActivity')"
                :checked="form.notificationsCfg.na2.enabled"
                :small="true"
              >
              </gl-checkbox>
            </div>
            <div class="form__group">
              <gl-select
                id="profile.notifications.na2.weekPeriodicity"
                v-skeletor:onChange="setNotifications"
                :label="$t('admins.mailFrequencyResumeActivity')"
                :placeholder="$t('admins.mailFrequencyResumeActivity')"
                :options.prop="getWeekNotifications"
                :mandatory="true"
                :value.prop="String(form.notificationsCfg.na2.weekPeriodicity)"
                :separator="true"
                :disabled="form.notificationsCfg.na2.enabled !== true"
                :errormessage="!form.notificationsCfg ? $t('account.errors.fieldRequired') : null"
              >
              </gl-select>
            </div>
          </div>
        </template>
      </div>
    </template>

    <!-- Form buttons -->
    <div class="gl-grid-form__buttons margin-auto">
      <gl-button
        id="profile.btn-save"
        color-primary
        :type="'submit'"
        :text="$t('account.forms.saveChanges')"
        :disabled="$v.$invalid || !dirtyFields"
        @onClick.prevent="updateUser(form)"
      ></gl-button>
    </div>
    <EditPopup
      v-if="fieldToEdit"
      :field-to-edit="fieldToEdit"
      :password-error="passwordError"
      @cancel="
        fieldToEdit = '';
        passwordError = '';
      "
      @done="updateData"
    ></EditPopup>
  </div>
</template>

<script>
import { required, email } from 'vuelidate/lib/validators';
import EditPopup from '@/components/Profile/EditPopup.vue';
import { mapState, mapActions, mapGetters } from 'vuex';
import { formatIds } from '../utils/functions.js';

export default {
  name: 'Profile',
  components: {
    EditPopup
  },
  data() {
    return {
      fieldToEdit: '',
      passwordError: '',
      previousPage: '',
      form: {
        name: null,
        surname: null,
        email: null,
        lang: null,
        notificationsCfg: {
          na2: {
            enabled: null,
            weekPeriodicity: null
          }
        }
      },
      weekList: null,
      errorMessages: {},
      dirtyFields: false
    };
  },
  computed: {
    ...mapState({
      userData: (state) => state.profile.profileData,
      profileForms: (state) => state.profile.profileForms,
      shouldShowNotificationsCfg() {
        return this.shouldShowNa2Cfg;
      },
      shouldShowNa2Cfg() {
        return ['clientAdmin', 'dachAdmin', 'hr', 'distributor', 'expert'].includes(
          this.userData.role
        );
      }
    }),
    ...mapGetters(['getLanguages', 'getWeekNotifications'])
  },
  async created() {
    if (!this.userData) {
      await this.getProfile();
    }
    const notificationsCfg = this.userData.notificationsCfg;
    this.form = {
      name: this.userData.name,
      surname: this.userData.surname,
      email: this.userData.email,
      lang: this.userData.lang,
      notificationsCfg: {
        na2: {
          enabled: notificationsCfg.na2.enabled,
          weekPeriodicity: notificationsCfg.na2.weekPeriodicity
        }
      }
    };
  },
  methods: {
    ...mapActions(['getProfile', 'changePassword', 'updateProfileData']),
    setValue(field, value, dirty) {
      this.setForm(formatIds(field), value, dirty);
      this.setDirtyField(dirty);
    },
    setForm(attr, value) {
      this.$set(this.form, attr, value);
    },
    setNotifications(attr, value, dirty) {
      if (formatIds(attr) === 'weekPeriodicity') {
        this.$set(this.form.notificationsCfg.na2, 'weekPeriodicity', Number(value));
      }

      if (formatIds(attr) === 'enabled') {
        this.$set(this.form.notificationsCfg.na2, 'enabled', value);
      }
      this.setDirtyField(dirty);
    },
    setDirtyField(dirty) {
      this.dirtyFields = dirty;
    },
    updateData(value) {
      if (this.fieldToEdit === 'password') {
        this.updatePassword(value);
      } else {
        this.updateUser(value);
      }
    },
    async updatePassword(value) {
      const { currentPassword, newPassword } = value;
      const data = {
        currentPassword: currentPassword,
        newPassword: newPassword,
        notification: {
          type: 'success',
          message: `profile.feedback.${this.fieldToEdit}`,
          fieldToEdit: this.fieldToEdit,
          show: true
        }
      };
      await this.changePassword(data);
      this.fieldToEdit = '';
    },
    async updateUser(value) {
      let updateData = {
        body: value,
        notification: {
          type: 'success',
          message: `feedback.success.editProfile`,
          show: true
        }
      };
      await this.updateProfileData(updateData);
      const messagePayload = {
        type: 'accounts',
        data: {
          action: 'CHANGE_LANG',
          lang: value.lang
        }
      };
      window.parent.postMessage(messagePayload, '*');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    editPassword(value) {
      this.fieldToEdit = value;
    }
  },
  validations: {
    form: {
      name: { required },
      surname: {},
      email: {
        required,
        email,
        isEmail(value) {
          if (value && !this.$v.form.email.email) {
            this.$set(this.errorMessages, 'email', this.$t('account.errors.invalidEmail'));
            return false;
          }
          return true;
        }
      },
      lang: {}
    }
  }
};
</script>
